import React, { useCallback, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  ConciergeWidget,
  ConciergeFloatingButton,
  ConciergeWidgetState,
  usePageContext,
} from "@brandclub/common-ui";
import { useConciergeWidget } from "@/utils/hooks/useConciergeWidget";
import { useIsConciergePage } from "@/utils/hooks/useIsConciergePage";
import { useIsConciergeEnabled } from "@brandclub/common-ui";
import { useAppSelector } from "@/redux/hooks";
// Import Tailwind CSS for concierge components
import "./concierge-tailwind.css";

// Create a query client for the widget
const queryClient = new QueryClient();

// Shared state for suggested pill queries between FloatingButton and Widget adapters
// Using a simple module-level state with subscribers pattern (avoids Zustand cross-bundle issues)
type PillQueryListener = (query: string | null) => void;
let pillQueryListeners: PillQueryListener[] = [];
let currentPillQuery: string | null = null;

const setPillQuery = (query: string | null) => {
  currentPillQuery = query;
  pillQueryListeners.forEach((listener) => listener(query));
};

const usePillQuery = () => {
  const [pillQuery, _setPillQuery] = useState<string | null>(currentPillQuery);

  React.useEffect(() => {
    const listener: PillQueryListener = (query) => {
      _setPillQuery(query);
    };
    pillQueryListeners.push(listener);
    return () => {
      pillQueryListeners = pillQueryListeners.filter((l) => l !== listener);
    };
  }, []);

  return { pillQuery, setPillQuery, clearPillQuery: () => setPillQuery(null) };
};

/**
 * Adapter that wraps Redux-based widget state to match ConciergeWidgetState interface
 */
const useWidgetStateAdapter = (): ConciergeWidgetState & {
  isMaximized: boolean;
  setMaximized: (v: boolean) => void;
} => {
  const { isOpen, isMaximized, toggle, closeWidget, openWidget, setMaximized } =
    useConciergeWidget();

  return {
    isOpen,
    isMaximized,
    toggle,
    close: closeWidget,
    open: openWidget,
    setMaximized,
  };
};

export const ConciergeWidgetAdapter: React.FC = React.memo(() => {
  const widgetState = useWidgetStateAdapter();
  const isConciergePage = useIsConciergePage();
  const pageContext = usePageContext();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { showWidget } = useIsConciergeEnabled({
    email: userProfile?.email,
  });
  const { pillQuery, clearPillQuery } = usePillQuery();

  const handleMaximize = useCallback(() => {
    widgetState.setMaximized(true);
  }, [widgetState]);

  const handleMinimize = useCallback(() => {
    widgetState.setMaximized(false);
  }, [widgetState]);

  const handleInitialQueryProcessed = useCallback(() => {
    clearPillQuery();
  }, [clearPillQuery]);

  // Hide widget when on the concierge page or when not in concierge split
  if (isConciergePage || !showWidget) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ConciergeWidget
        widgetState={widgetState}
        pageContext={pageContext}
        showMaximize={true}
        isMaximized={widgetState.isMaximized}
        onMaximize={handleMaximize}
        onMinimize={handleMinimize}
        isConciergeEnabled={showWidget}
        initialQuery={pillQuery || undefined}
        onInitialQueryProcessed={handleInitialQueryProcessed}
      />
    </QueryClientProvider>
  );
});

export const ConciergeFloatingButtonAdapter: React.FC = React.memo(() => {
  const widgetState = useWidgetStateAdapter();
  const isConciergePage = useIsConciergePage();
  const userProfile = useAppSelector(({ userProfile }) => userProfile);
  const { showWidget } = useIsConciergeEnabled({
    email: userProfile?.email,
  });
  const { setPillQuery } = usePillQuery();

  const handleSuggestedPillClick = useCallback(
    (message: string) => {
      setPillQuery(message);
    },
    [setPillQuery]
  );

  return (
    <ConciergeFloatingButton
      widgetState={widgetState}
      isConciergeEnabled={showWidget}
      hideOnConciergePage={isConciergePage}
      isMaximized={widgetState.isMaximized}
      onSuggestedPillClick={handleSuggestedPillClick}
    />
  );
});
